// Android bridge — talks to the Kotlin WebBridge registered as
// `AndroidBridge` via WebView.addJavascriptInterface. Unlike WKWebView's
// reply-handler model, @JavascriptInterface methods can't return promises
// across the JNI boundary, so we attach correlation IDs to each call and
// resolve via a global `__bridgeReply(id, result, error)` that the Kotlin
// side calls back through `WebView.evaluateJavascript`.

import type { Bridge } from './index'
import type { BridgeMethods } from './types'
import { createEmitter } from './emitter'

type AndroidHandler = { postMessage(body: string): void }

type Pending = {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
}

export function createAndroidBridge(): Bridge {
  const handler = (window as unknown as { AndroidBridge: AndroidHandler })
    .AndroidBridge

  const emitter = createEmitter()
  const pending = new Map<string, Pending>()
  let nextID = 1

  ;(window as unknown as {
    __bridgeReply?: (id: string, result: unknown, error: string | null) => void
  }).__bridgeReply = (id, result, error) => {
    const entry = pending.get(id)
    if (!entry) return
    pending.delete(id)
    if (error) entry.reject(new Error(error))
    else entry.resolve(result)
  }

  return {
    isNative: true,
    isAndroid: true,
    call<K extends keyof BridgeMethods>(
      method: K,
      args?: BridgeMethods[K]['args'],
    ): Promise<BridgeMethods[K]['result']> {
      return new Promise((resolve, reject) => {
        const id = String(nextID++)
        pending.set(id, {
          resolve: resolve as (v: unknown) => void,
          reject,
        })
        handler.postMessage(JSON.stringify({ id, method, args: args ?? {} }))
      })
    },
    on: emitter.on,
    emit: emitter.emit,
  }
}
